import type { FieldError, FieldValues } from 'react-hook-form';

import type { FieldConfig } from './types';

import { FormCheckbox } from '../Form/FormCheckbox';
import { FormInput } from '../Form/FormInput';
import { FormJsonInput } from '../Form/FormJsonInput';
import { FormSelect } from '../Form/FormSelect';

interface SmartFormFieldProps<T extends FieldValues = FieldValues> {
  field: FieldConfig<T>;
  value: any;
  onChange: (value: any) => void;
  error?: FieldError;
}

export const SmartFormField = <T extends FieldValues>({ field, value, onChange, error }: SmartFormFieldProps<T>) => {
  const commonProps = {
    name: field.name,
    label: field.label,
    value: value ?? field.defaultValue ?? '',
    onChange,
    error: error?.message,
    required: field.required,
    placeholder: field.placeholder,
    helperText: field.helperText,
    readOnly: field.readOnly,
    disabled: field.disabled,
    ...field.componentProps,
  } as const;

  if (field.type === 'boolean') {
    return <FormCheckbox key={field.name} {...commonProps} />;
  }

  if (field.type === 'select' && field.options) {
    return <FormSelect key={field.name} {...commonProps} options={field.options} />;
  }

  if (field.type === 'json') {
    return <FormJsonInput key={field.name} {...commonProps} />;
  }

  if ('component' in field && field.component) {
    return <FormInput key={field.name} {...commonProps} as={field.component} />;
  }

  return <FormInput key={field.name} {...commonProps} type={field.type || 'text'} />;
};
